import { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import {
  Clapperboard, Loader2, Sparkles, Plus, Trash2, Wand2, Coins, ArrowUp, ArrowDown,
  ShieldCheck, AlertTriangle, CheckCircle2, MessageSquare, Film, ArrowRight, ArrowLeft, Lightbulb,
  FileText, Clock, Users, ScrollText, Mic, Video as VideoIcon, Volume2, Download, Music, Share2, Languages,
  Image as ImageIcon, Palette, MessageCircle, Instagram, Star, GripVertical,
} from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { VIDEO_STYLES } from "@/lib/options";
import { filmSceneCredits, filmTotalCredits, fmtCredits, fmtCreditsUsd } from "@/lib/cost";
import StatusBadge from "@/components/StatusBadge";

const STORY_TEMPLATES = [
  { id: "thriller", label: "Thriller", icon: "🔍", text: "Un detective persigue a un sospechoso escurridizo por una ciudad de neón bajo la lluvia, mientras descubre que el caso lo toca de cerca." },
  { id: "comedia", label: "Comedia", icon: "😄", text: "Dos amigos torpes montan un negocio disparatado y todo lo que puede salir mal, sale mal, de la forma más divertida." },
  { id: "anuncio", label: "Anuncio", icon: "📢", text: "Un anuncio corto y llamativo que presenta un producto nuevo, mostrando cómo cambia la vida del cliente en 30 segundos con un cierre memorable." },
  { id: "drama", label: "Drama", icon: "🎭", text: "Una historia emotiva sobre una persona que enfrenta una decisión difícil y encuentra la fuerza para reconciliarse con su pasado." },
  { id: "terror", label: "Terror", icon: "👻", text: "Un grupo entra a una casa abandonada y algo invisible los acecha; la tensión crece hasta un giro final aterrador." },
  { id: "aventura", label: "Aventura", icon: "🗺️", text: "Un explorador sigue un mapa antiguo hacia un tesoro escondido, superando trampas y paisajes espectaculares." },
];

const STEPS = [
  { id: 1, label: "Idea", icon: Lightbulb },
  { id: 2, label: "Guion", icon: ScrollText },
  { id: 3, label: "Minutos", icon: Clock },
  { id: 4, label: "Escenas", icon: Film },
  { id: 5, label: "Personajes", icon: Users },
  { id: 6, label: "Costo", icon: Coins },
  { id: 7, label: "Generar", icon: Sparkles },
];
const MINUTE_OPTS = [1, 2, 3, 5, 10, 15, 20, 25];
const MUSIC_GENRES = [
  { id: "epica", label: "Épica", prompt: "épica orquestal cinematográfica, tensa e inspiradora" },
  { id: "suspenso", label: "Suspenso", prompt: "suspenso oscuro, sintetizadores tensos, thriller" },
  { id: "accion", label: "Acción", prompt: "acción intensa, percusión potente, ritmo rápido" },
  { id: "romantica", label: "Romántica", prompt: "romántica emotiva, piano suave y cuerdas cálidas" },
  { id: "lofi", label: "Lo-fi", prompt: "lofi chill relajado, beats suaves" },
  { id: "terror", label: "Terror", prompt: "terror inquietante, drones oscuros y disonancias" },
  { id: "synthwave", label: "Synthwave", prompt: "synthwave retro neón, ochentero, atmosférico" },
];
const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : String(Math.random()));

export default function ShortFilmPanel({ projects, characters, reload }) {
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState("");
  const [style, setStyle] = useState("cinematic");
  const [idea, setIdea] = useState("");
  const [guion, setGuion] = useState("");
  const [minutes, setMinutes] = useState(2);
  const [scenes, setScenes] = useState([]);
  const [speakerMap, setSpeakerMap] = useState({}); // { speakerName: characterId }
  const [narratorVoiceId, setNarratorVoiceId] = useState(""); // "" = narrador neutro
  const [autoAssemble, setAutoAssemble] = useState(true);
  const [previewBusy, setPreviewBusy] = useState(null);
  const [bgmMode, setBgmMode] = useState("off"); // off | mood | upload
  const [bgmPrompt, setBgmPrompt] = useState("");
  const [bgmPath, setBgmPath] = useState("");
  const [bgmUploading, setBgmUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publicLink, setPublicLink] = useState("");
  const [dubLang, setDubLang] = useState("");
  const [dubbing, setDubbing] = useState(false);
  const [bgmVolume, setBgmVolume] = useState(0.32);
  const [guionPreviewBusy, setGuionPreviewBusy] = useState(false);
  const [sceneFrames, setSceneFrames] = useState({}); // {sceneKey: imagePath}
  const [frameBusy, setFrameBusy] = useState(null);
  const [coverOpen, setCoverOpen] = useState(false);
  const [coverColor, setCoverColor] = useState("blanco");
  const [coverScene, setCoverScene] = useState(0);
  const [coverBusy, setCoverBusy] = useState(false);
  const [coverPreview, setCoverPreview] = useState("");
  const [burnSubs, setBurnSubs] = useState(false);
  const [myTemplates, setMyTemplates] = useState([]);
  const [savingTpl, setSavingTpl] = useState(false);
  const [regenBusy, setRegenBusy] = useState(null);

  useEffect(() => {
    api.get("/story-templates").then((r) => setMyTemplates(r.data || [])).catch(() => {});
  }, []);

  const [busyGuion, setBusyGuion] = useState(false);
  const [busyScenes, setBusyScenes] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastId, setLastId] = useState(null);
  const [checking, setChecking] = useState(false);

  const current = projects.find((p) => p.id === lastId);
  const charById = (id) => characters.find((c) => c.id === id);
  const charReady = (c) => !!(c && c.photo_path && c.voice_path);
  const sortedChars = useMemo(
    () => [...characters].sort((a, b) => (b.favorite ? 1 : 0) - (a.favorite ? 1 : 0)),
    [characters]
  );
  const [dragScene, setDragScene] = useState(null);

  const toggleFavorite = async (cid) => {
    try { await api.post(`/characters/${cid}/favorite`); await reload(); }
    catch { toast.error("No se pudo cambiar el favorito"); }
  };

  const speakerNames = useMemo(() => {
    const set = [];
    scenes.forEach((s) => {
      if (s.type === "dialogue" && (s.speaker || "").trim() && !set.includes(s.speaker.trim()))
        set.push(s.speaker.trim());
    });
    return set;
  }, [scenes]);

  const effectiveType = (s) => {
    if (s.type !== "dialogue" || !(s.dialogue || "").trim()) return "ambient";
    const cid = speakerMap[(s.speaker || "").trim()];
    return charReady(charById(cid)) ? "dialogue" : "ambient";
  };
  const costScenes = useMemo(
    () => scenes.map((s) => ({ ...s, type: effectiveType(s) })),
    [scenes, speakerMap, characters] // eslint-disable-line
  );
  const totalCredits = filmTotalCredits(costScenes);

  const generateGuion = async () => {
    if (!idea.trim()) { toast.error("Escribe tu idea primero"); return; }
    setBusyGuion(true);
    try {
      const names = characters.map((c) => c.name).join(", ");
      const r = await api.post("/ai/guion", { idea, style, characters: names });
      setGuion(r.data.guion || "");
      toast.success("Guion generado. Revísalo y edítalo si quieres.");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo generar el guion"); }
    finally { setBusyGuion(false); }
  };

  const generateScenes = async () => {
    if (!guion.trim()) { toast.error("Primero genera o escribe el guion"); return; }
    setBusyScenes(true);
    try {
      const names = characters.map((c) => c.name).join(", ");
      const r = await api.post("/ai/storyboard", {
        synopsis: guion, minutes: Number(minutes || 2), style, characters: names, typed: true,
      });
      const list = (r.data.scenes || []).map((s) => ({
        key: uid(),
        description: s.description || "",
        duration: s.duration || "6",
        type: s.type === "dialogue" ? "dialogue" : "ambient",
        dialogue: s.dialogue || "",
        speaker: s.speaker || "",
        narration: "",
      }));
      setScenes(list);
      const map = {};
      const favReady = characters.find((c) => c.favorite && c.photo_path && c.voice_path);
      list.forEach((s) => {
        if (s.type === "dialogue" && s.speaker) {
          const match = characters.find((c) => c.name.toLowerCase() === s.speaker.toLowerCase());
          if (match) map[s.speaker.trim()] = match.id;
          else if (favReady) map[s.speaker.trim()] = favReady.id;
        }
      });
      setSpeakerMap((prev) => ({ ...map, ...prev }));
      const dlg = list.filter((s) => s.type === "dialogue").length;
      toast.success(`Storyboard: ${list.length} escenas (${dlg} con diálogo). Avanza para revisar.`);
      setStep(4);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudieron generar las escenas"); }
    finally { setBusyScenes(false); }
  };

  const updateScene = (i, patch) => setScenes(scenes.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const removeScene = (i) => setScenes(scenes.filter((_, idx) => idx !== i));
  const addScene = () => setScenes([...scenes, { key: uid(), description: "", duration: "6", type: "ambient", dialogue: "", speaker: "", narration: "" }]);

  const saveTemplate = async () => {
    if (!idea.trim()) { toast.error("Escribe una idea para guardar"); return; }
    setSavingTpl(true);
    try {
      const label = idea.trim().slice(0, 30) + (idea.trim().length > 30 ? "…" : "");
      const r = await api.post("/story-templates", { label, text: idea.trim() });
      setMyTemplates((prev) => [r.data, ...prev]);
      toast.success("Plantilla guardada");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSavingTpl(false); }
  };

  const deleteTemplate = async (id) => {
    try { await api.delete(`/story-templates/${id}`); setMyTemplates((prev) => prev.filter((t) => t.id !== id)); }
    catch { toast.error("No se pudo borrar"); }
  };

  const regenerateScene = async (i) => {
    if (!current) return;
    setRegenBusy(i);
    try {
      await api.post(`/projects/${current.id}/scenes/${i}/regenerate`);
      await reload();
      toast.success(`Regenerando solo la escena ${i + 1}…`);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo regenerar la escena"); }
    finally { setRegenBusy(null); }
  };

  const previewSceneFrame = async (i) => {
    const s = scenes[i];
    if (!s || !s.description.trim()) { toast.error("Escribe la descripción de la escena"); return; }
    setFrameBusy(s.key);
    try {
      const speakerId = s.type === "dialogue" ? speakerMap[(s.speaker || "").trim()] : null;
      const r = await api.post("/ai/scene-frame", { description: s.description, character_id: speakerId || null });
      setSceneFrames((prev) => ({ ...prev, [s.key]: r.data.image_path }));
      toast.success("Muestra lista");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo generar la muestra"); }
    finally { setFrameBusy(null); }
  };

  const generateCover = async () => {
    if (!current) return;
    setCoverBusy(true);
    try {
      const r = await api.post(`/projects/${current.id}/cover`, { scene_index: coverScene, title_color: coverColor });
      setCoverPreview(r.data.poster_path);
      await reload();
      toast.success("Portada actualizada");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo generar la portada"); }
    finally { setCoverBusy(false); }
  };

  const shareTo = (network) => {
    if (!publicLink) return;
    const text = encodeURIComponent(`Mira mi cortometraje "${current?.title || ""}" hecho con Metáfora Play: `);
    if (network === "whatsapp") {
      window.open(`https://wa.me/?text=${text}${encodeURIComponent(publicLink)}`, "_blank");
    } else if (navigator.share) {
      navigator.share({ title: current?.title || "Cortometraje", url: publicLink }).catch(() => {});
    } else {
      navigator.clipboard.writeText(publicLink).then(() => toast.success(`Enlace copiado — pégalo en ${network === "instagram" ? "Instagram" : "TikTok"}`)).catch(() => {});
    }
  };

  const previewGuion = async () => {
    if (!guion.trim()) { toast.error("Genera o escribe el guion primero"); return; }
    setGuionPreviewBusy(true);
    try {
      const r = await api.post("/ai/guion-preview", { guion, voice_id: narratorVoiceId || null });
      const id = r.data.id;
      for (let k = 0; k < 30; k++) {
        await new Promise((res) => setTimeout(res, 2000));
        const st = await api.get(`/ai/voice-preview/${id}`);
        if (st.data.status === "ready" && st.data.audio_path) {
          new Audio(mediaUrl(st.data.audio_path)).play().catch(() => {});
          toast.success("Leyendo el guion en voz alta…");
          return;
        }
        if (st.data.status === "failed") { toast.error("No se pudo generar la lectura"); return; }
      }
      toast.error("La lectura tardó demasiado");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo leer el guion"); }
    finally { setGuionPreviewBusy(false); }
  };

  const playVoicePreview = async (cid) => {
    if (!cid) return;
    setPreviewBusy(cid);
    try {
      const r = await api.post(`/characters/${cid}/voice-preview`);
      const id = r.data.id;
      for (let k = 0; k < 30; k++) {
        await new Promise((res) => setTimeout(res, 2000));
        const st = await api.get(`/ai/voice-preview/${id}`);
        if (st.data.status === "ready" && st.data.audio_path) {
          new Audio(mediaUrl(st.data.audio_path)).play().catch(() => {});
          toast.success("Reproduciendo la voz…");
          return;
        }
        if (st.data.status === "failed") { toast.error("No se pudo generar la vista previa"); return; }
      }
      toast.error("La vista previa tardó demasiado");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo escuchar la voz"); }
    finally { setPreviewBusy(null); }
  };
  const moveScene = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= scenes.length) return;
    const next = [...scenes];
    [next[i], next[j]] = [next[j], next[i]];
    setScenes(next);
  };

  const submit = async () => {
    const valid = costScenes.filter((s) => s.description.trim());
    if (!valid.length) { toast.error("No hay escenas para generar"); return; }
    setSubmitting(true);
    try {
      const castIds = [...new Set(valid.filter((s) => s.type === "dialogue")
        .map((s) => speakerMap[(s.speaker || "").trim()]).filter(Boolean))];
      const payloadScenes = valid.map((s) => {
        if (s.type === "dialogue") {
          const sid = speakerMap[(s.speaker || "").trim()];
          return { description: s.description, duration: s.duration, type: "dialogue",
                   dialogue: s.dialogue, speaker_id: sid, character_ids: [sid] };
        }
        return { description: s.description, duration: s.duration, type: "ambient",
                 narration: (s.narration || "").trim() };
      });
      const r = await api.post("/shortfilms", {
        title: title.trim() || "Mi cortometraje",
        style, continuity: true, narration: false, lipsync: false,
        anchor_face: true, auto_analyze: !!castIds.length, quality: "pro",
        character_ids: castIds, character_id: castIds[0] || null,
        narrator_voice_id: narratorVoiceId || null,
        auto_assemble: autoAssemble,
        bgm_prompt: bgmMode === "mood" ? bgmPrompt.trim() : "",
        bgm_audio_path: bgmMode === "upload" ? bgmPath : "",
        bgm_volume: bgmVolume,
        burn_subs: burnSubs,
        scenes: payloadScenes,
        idempotency_key: uid(),
      });
      setLastId(r.data.id);
      await reload();
      toast.success("Generando cortometraje… audio primero y lip-sync nativo en los diálogos.");
      setStep(7);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al generar");
    } finally { setSubmitting(false); }
  };

  const runCheck = async () => {
    if (!current) return;
    setChecking(true);
    try {
      const r = await api.post(`/projects/${current.id}/continuity/check`);
      await reload();
      const bad = (r.data.scenes || []).filter((s) => !s.match).length;
      toast.success(bad ? `Continuidad ${r.data.overall}/100 · ${bad} escena(s) con desvío` : `Continuidad ${r.data.overall}/100 · todo consistente ✅`);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo analizar la continuidad"); }
    finally { setChecking(false); }
  };

  const uploadBgm = async (file) => {
    if (!file) return;
    setBgmUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/audio", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBgmPath(r.data.path);
      toast.success("Música cargada");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo subir la música"); }
    finally { setBgmUploading(false); }
  };

  const publishToCommunity = async () => {
    if (!current) return;
    setPublishing(true);
    try {
      await api.post(`/projects/${current.id}/publish`, { category: "corto" });
      const link = `${window.location.origin}/comunidad/${current.id}`;
      setPublicLink(link);
      try { await navigator.clipboard.writeText(link); toast.success("¡Publicado! Enlace copiado al portapapeles."); }
      catch { toast.success("¡Publicado en la Comunidad!"); }
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo publicar"); }
    finally { setPublishing(false); }
  };

  const dubFilm = async (language) => {
    if (!current) return;
    setDubbing(true);
    try {
      if (language === "both") {
        await api.post(`/projects/${current.id}/dub`, { language: "en", idempotency_key: uid() });
        const r2 = await api.post(`/projects/${current.id}/dub`, { language: "spanglish", idempotency_key: uid() });
        setLastId(r2.data.id);
        toast.success("Doblando a Inglés y Spanglish… aparecerán en Proyectos (el Español ya es este corto).");
      } else {
        const r = await api.post(`/projects/${current.id}/dub`, { language, idempotency_key: uid() });
        setLastId(r.data.id);
        toast.success(`Doblando a ${language === "en" ? "Inglés" : "Spanglish"}… reutilizando el guion.`);
      }
      setDubLang("");
      await reload();
      setStep(7);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo doblar"); }
    finally { setDubbing(false); }
  };

  const downloadSrt = async () => {
    if (!current) return;
    try {
      const res = await api.get(`/projects/${current.id}/subtitles.srt`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${current.title || "cortometraje"}.srt`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudieron generar los subtítulos"); }
  };

  const canNext = () => {
    if (step === 1) return idea.trim().length > 3;
    if (step === 2) return guion.trim().length > 10;
    if (step === 3) return scenes.length > 0;
    if (step === 4) return scenes.some((s) => s.description.trim());
    if (step === 5) return true;
    if (step === 6) return true;
    return false;
  };
  const goNext = () => { if (step < 7) setStep(step + 1); };
  const goBack = () => { if (step > 1) setStep(step - 1); };

  const unreadySpeakers = speakerNames.filter((n) => !charReady(charById(speakerMap[n])));
  const dialogueCount = costScenes.filter((s) => s.type === "dialogue").length;

  return (
    <div className="max-w-3xl mx-auto space-y-6 fade-up">
      <div className="flex items-center gap-3">
        <Clapperboard className="w-6 h-6 text-[#FF334B]" />
        <h2 className="font-head text-2xl sm:text-3xl font-bold tracking-tight">Cortometraje · flujo guiado</h2>
      </div>
      <p className="text-sm text-[#94A3B8] -mt-2">
        Audio primero, como una serie real: escribes la idea, la IA arma el guion y las escenas, y los diálogos nacen con lip-sync nativo. Nunca se gasta un crédito sin tu confirmación.
      </p>

      <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto pb-1" data-testid="sf-stepper">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const done = step > s.id;
          const active = step === s.id;
          return (
            <div key={s.id} className="flex items-center gap-1 sm:gap-2 shrink-0">
              <button
                data-testid={`sf-step-${s.id}`}
                onClick={() => s.id < step && setStep(s.id)}
                disabled={s.id > step}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs font-semibold transition-colors ${
                  active ? "bg-[#FF334B] border-[#FF334B] text-white"
                  : done ? "border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10"
                  : "border-white/10 text-[#64748B]"}`}>
                {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline">{s.label}</span>
                <span className="sm:hidden">{s.id}</span>
              </button>
              {i < STEPS.length - 1 && <div className="w-3 sm:w-5 h-px bg-white/10" />}
            </div>
          );
        })}
      </div>

      <div className="mfp-card p-6 space-y-6">
        {step === 1 && (
          <div className="space-y-5 fade-up" data-testid="sf-step-idea">
            <div>
              <label className="mono-label">Título</label>
              <input data-testid="sf-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="Nombre del cortometraje"
                className="mt-2 w-full bg-[#0B0C13] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#FF334B]" />
            </div>
            <div>
              <label className="mono-label">Estilo visual</label>
              <div className="mt-3 flex flex-wrap gap-2">
                {VIDEO_STYLES.map((s) => (
                  <button key={s.id} data-testid={`sf-style-${s.id}`} onClick={() => setStyle(s.id)}
                    className={`px-3.5 py-1.5 rounded-full border text-sm transition-colors ${style === s.id ? "bg-[#FF334B] border-[#FF334B] text-white" : "border-white/12 text-[#94A3B8] hover:border-[#FF334B]/50"}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="mono-label">Tu idea</label>
              <p className="text-xs text-[#64748B] mt-1">Cuéntale a la IA de qué trata. Ella escribirá el guion. Esto es gratis.</p>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="sf-templates">
                {STORY_TEMPLATES.map((t) => (
                  <button key={t.id} data-testid={`sf-template-${t.id}`} onClick={() => setIdea(t.text)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/12 text-xs text-[#94A3B8] hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors">
                    <span>{t.icon}</span> {t.label}
                  </button>
                ))}
                {myTemplates.map((t) => (
                  <span key={t.id} data-testid={`sf-mytemplate-${t.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#FF9F2E]/40 text-xs text-[#FF9F2E]">
                    <button onClick={() => setIdea(t.text)} className="hover:underline">⭐ {t.label}</button>
                    <button data-testid={`sf-mytemplate-del-${t.id}`} onClick={() => deleteTemplate(t.id)} className="text-[#64748B] hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                  </span>
                ))}
                {idea.trim().length > 8 && (
                  <button data-testid="sf-save-template" onClick={saveTemplate} disabled={savingTpl}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-dashed border-white/20 text-xs text-[#94A3B8] hover:border-[#FF9F2E] hover:text-[#FF9F2E] disabled:opacity-50">
                    {savingTpl ? <Loader2 className="w-3 h-3 spin" /> : <Plus className="w-3 h-3" />} Guardar como plantilla
                  </button>
                )}
              </div>
              <textarea data-testid="sf-idea-input" value={idea} onChange={(e) => setIdea(e.target.value)} rows={5}
                placeholder="Ej. Un detective persigue a un ladrón fantasma por una ciudad de neón mientras recuerda su pasado…"
                className="mt-2 w-full bg-[#0B0C13] border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#FF334B] resize-none" />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 fade-up" data-testid="sf-step-guion">
            <div className="flex items-center justify-between gap-3">
              <div>
                <label className="mono-label">Guion</label>
                <p className="text-xs text-[#64748B] mt-1">La IA convierte tu idea en un guion con narración y diálogos. Edítalo libremente. Gratis.</p>
              </div>
              <button data-testid="sf-generate-guion" onClick={generateGuion} disabled={busyGuion}
                className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full bg-[#00F0FF] text-black hover:opacity-90 transition-opacity disabled:opacity-50 shrink-0">
                {busyGuion ? <Loader2 className="w-4 h-4 spin" /> : <Wand2 className="w-4 h-4" />}
                {guion ? "Regenerar" : "Generar guion"}
              </button>
            </div>
            {guion.trim() && (
              <button data-testid="sf-preview-guion" onClick={previewGuion} disabled={guionPreviewBusy}
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-full border border-[#FF9F2E]/50 text-[#FF9F2E] hover:bg-[#FF9F2E]/10 transition-colors disabled:opacity-50 self-start">
                {guionPreviewBusy ? <Loader2 className="w-3.5 h-3.5 spin" /> : <Volume2 className="w-3.5 h-3.5" />} Escuchar guion
              </button>
            )}
            <textarea data-testid="sf-guion-text" value={guion} onChange={(e) => setGuion(e.target.value)} rows={14}
              placeholder="Aquí aparecerá el guion generado. También puedes escribirlo tú."
              className="w-full bg-[#0B0C13] border border-white/10 rounded-xl px-4 py-3 text-sm leading-relaxed outline-none focus:border-[#FF334B] resize-none" />
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5 fade-up" data-testid="sf-step-minutes">
            <div>
              <label className="mono-label">¿De cuántos minutos lo quieres?</label>
              <p className="text-xs text-[#64748B] mt-1">La IA dividirá el guion en escenas para llegar a esa duración.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {MINUTE_OPTS.map((m) => (
                  <button key={m} data-testid={`sf-minutes-${m}`} onClick={() => setMinutes(m)}
                    className={`px-4 py-2 rounded-full border text-sm font-semibold transition-colors ${minutes === m ? "bg-[#FF334B] border-[#FF334B] text-white" : "border-white/12 text-[#94A3B8] hover:border-[#FF334B]/50"}`}>
                    {m} min
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-[#64748B] mt-3">≈ {Math.round((minutes * 60) / 6)} escenas aprox. · aún no se genera ningún video.</p>
            </div>
            <button data-testid="sf-generate-scenes" onClick={generateScenes} disabled={busyScenes}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#00F0FF] text-black font-semibold py-3 rounded-full hover:opacity-90 transition-opacity disabled:opacity-50">
              {busyScenes ? <Loader2 className="w-5 h-5 spin" /> : <Film className="w-5 h-5" />}
              Generar escenas desde el guion
            </button>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4 fade-up" data-testid="sf-step-scenes">
            <div className="flex items-center justify-between">
              <div>
                <label className="mono-label">Escenas · {scenes.length}</label>
                <p className="text-xs text-[#64748B] mt-1">Marca cuáles son diálogo (personaje habla en cámara) o ambiente. Arrastra o usa las flechas para reordenar.</p>
              </div>
              <button data-testid="sf-add-scene" onClick={addScene}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF334B] hover:text-[#E0223A]">
                <Plus className="w-4 h-4" /> Escena
              </button>
            </div>
            {scenes.map((s, i) => (
              <div key={s.key} draggable
                onDragStart={() => setDragScene(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (dragScene !== null && dragScene !== i) { const n = [...scenes]; const [it] = n.splice(dragScene, 1); n.splice(i, 0, it); setScenes(n); } setDragScene(null); }}
                onDragEnd={() => setDragScene(null)}
                className={`mfp-elev rounded-xl p-4 space-y-3 ${dragScene === i ? "opacity-40 ring-2 ring-[#FF334B]" : ""}`} data-testid={`sf-scene-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="font-display text-xl text-[#FF9F2E] inline-flex items-center gap-2">
                    <GripVertical className="w-4 h-4 text-[#64748B] cursor-grab" data-testid={`sf-scene-drag-${i}`} /> ESCENA {i + 1}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#64748B]" data-testid={`sf-scene-cost-${i}`}>
                      {fmtCredits(filmSceneCredits({ ...s, type: effectiveType(s) }))}
                    </span>
                    <button data-testid={`sf-scene-up-${i}`} onClick={() => moveScene(i, -1)} disabled={i === 0}
                      className="text-[#64748B] hover:text-white disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                    <button data-testid={`sf-scene-down-${i}`} onClick={() => moveScene(i, 1)} disabled={i === scenes.length - 1}
                      className="text-[#64748B] hover:text-white disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                    <select data-testid={`sf-scene-duration-${i}`} value={s.duration} onChange={(e) => updateScene(i, { duration: e.target.value })}
                      className="bg-[#0B0C13] border border-white/10 rounded-lg px-2 py-1.5 text-sm outline-none">
                      <option value="5">5s</option>
                      <option value="10">10s</option>
                    </select>
                    {scenes.length > 1 && (
                      <button data-testid={`sf-remove-scene-${i}`} onClick={() => removeScene(i)} className="text-[#64748B] hover:text-red-400">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`sf-type-ambient-${i}`} onClick={() => updateScene(i, { type: "ambient" })}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${s.type !== "dialogue" ? "bg-[#FF9F2E]/15 border-[#FF9F2E] text-[#FF9F2E]" : "border-white/12 text-[#64748B]"}`}>
                    <VideoIcon className="w-3.5 h-3.5" /> Ambiente / acción
                  </button>
                  <button data-testid={`sf-type-dialogue-${i}`} onClick={() => updateScene(i, { type: "dialogue" })}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${s.type === "dialogue" ? "bg-[#00F0FF]/15 border-[#00F0FF] text-[#00F0FF]" : "border-white/12 text-[#64748B]"}`}>
                    <MessageSquare className="w-3.5 h-3.5" /> Diálogo en cámara
                  </button>
                </div>
                <textarea data-testid={`sf-scene-desc-${i}`} value={s.description} onChange={(e) => updateScene(i, { description: e.target.value })}
                  rows={2} placeholder="Qué se ve en esta escena (acción, lugar, cámara, luz)"
                  className="w-full bg-[#0B0C13] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#FF334B]" />
                <div className="flex items-center gap-3 flex-wrap">
                  <button data-testid={`sf-scene-frame-btn-${i}`} onClick={() => previewSceneFrame(i)} disabled={frameBusy === s.key}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-white/12 text-[#94A3B8] hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors disabled:opacity-50">
                    {frameBusy === s.key ? <Loader2 className="w-3.5 h-3.5 spin" /> : <ImageIcon className="w-3.5 h-3.5" />} Ver muestra
                  </button>
                  <span className="text-[11px] text-[#64748B]">Un fotograma de prueba, sin gastar créditos ni saldo Fal.</span>
                </div>
                {sceneFrames[s.key] && (
                  <img src={mediaUrl(sceneFrames[s.key])} alt="muestra" data-testid={`sf-scene-frame-img-${i}`}
                    className="w-full rounded-lg border border-white/10 aspect-video object-cover" />
                )}
                {s.type === "dialogue" && (
                  <div className="space-y-2 rounded-lg border border-[#00F0FF]/25 bg-[#00F0FF]/5 p-3" data-testid={`sf-dialogue-box-${i}`}>
                    <input data-testid={`sf-speaker-name-${i}`} value={s.speaker} onChange={(e) => updateScene(i, { speaker: e.target.value })}
                      placeholder="¿Quién habla? (nombre)"
                      className="w-full bg-[#0B0C13] border border-white/10 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:border-[#00F0FF]" />
                    <textarea data-testid={`sf-dialogue-text-${i}`} value={s.dialogue} onChange={(e) => updateScene(i, { dialogue: e.target.value })}
                      rows={2} placeholder="Lo que dice el personaje (se generará con su voz y lip-sync nativo)"
                      className="w-full bg-[#0B0C13] border border-white/10 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:border-[#00F0FF]" />
                  </div>
                )}
                {s.type !== "dialogue" && (
                  <div className="space-y-1.5 rounded-lg border border-[#FF9F2E]/25 bg-[#FF9F2E]/5 p-3" data-testid={`sf-narration-box-${i}`}>
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#FF9F2E]"><Volume2 className="w-3.5 h-3.5" /> Narración en off (opcional)</span>
                    <textarea data-testid={`sf-narration-text-${i}`} value={s.narration || ""} onChange={(e) => updateScene(i, { narration: e.target.value })}
                      rows={2} placeholder="Voz en off para esta escena (estilo documental/serie). Déjalo vacío para solo imagen."
                      className="w-full bg-[#0B0C13] border border-white/10 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:border-[#FF9F2E]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {step === 5 && (
          <div className="space-y-4 fade-up" data-testid="sf-step-cast">
            <div>
              <label className="mono-label">Personajes que hablan</label>
              <p className="text-xs text-[#64748B] mt-1">Asigna cada personaje con diálogo a uno de tu elenco. Debe tener <b>foto y voz</b> para el lip-sync nativo. Prepáralos en la pestaña <span className="text-[#FF334B]">Personajes</span>.</p>
            </div>
            <div className="mfp-elev rounded-xl p-4 space-y-2" data-testid="sf-narrator-box">
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF9F2E]"><Volume2 className="w-4 h-4" /> Voz del narrador (voz en off)</span>
              <div className="flex items-center gap-2 flex-wrap">
                <select data-testid="sf-narrator-select" value={narratorVoiceId} onChange={(e) => setNarratorVoiceId(e.target.value)}
                  className="bg-[#0B0C13] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#FF9F2E]">
                  <option value="">Narrador neutro (voz de la IA)</option>
                  {characters.filter((c) => c.voice_path).sort((a, b) => (b.favorite ? 1 : 0) - (a.favorite ? 1 : 0)).map((ch) => <option key={ch.id} value={ch.id}>{ch.favorite ? "⭐ " : ""}Voz de {ch.name}</option>)}
                </select>
                {narratorVoiceId && (
                  <button data-testid="sf-narrator-preview" onClick={() => playVoicePreview(narratorVoiceId)} disabled={previewBusy === narratorVoiceId}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-[#FF9F2E]/40 text-[#FF9F2E] hover:bg-[#FF9F2E]/10 disabled:opacity-50">
                    {previewBusy === narratorVoiceId ? <Loader2 className="w-3.5 h-3.5 spin" /> : <Volume2 className="w-3.5 h-3.5" />} Escuchar
                  </button>
                )}
              </div>
            </div>
            {speakerNames.length === 0 && (
              <p className="text-sm text-[#64748B]" data-testid="sf-no-speakers">No hay escenas con diálogo. Puedes generar solo con ambiente y música.</p>
            )}
            {speakerNames.map((name) => {
              const cid = speakerMap[name];
              const c = charById(cid);
              return (
                <div key={name} className="mfp-elev rounded-xl p-4 flex items-center gap-3 flex-wrap" data-testid={`sf-speaker-map-${name}`}>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#00F0FF]">
                    <MessageSquare className="w-4 h-4" /> {name}
                  </span>
                  <ArrowRight className="w-4 h-4 text-[#64748B]" />
                  <select data-testid={`sf-speaker-select-${name}`} value={cid || ""} onChange={(e) => setSpeakerMap({ ...speakerMap, [name]: e.target.value })}
                    className="bg-[#0B0C13] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#00F0FF]">
                    <option value="">— elegir personaje —</option>
                    {sortedChars.map((ch) => <option key={ch.id} value={ch.id}>{ch.favorite ? "⭐ " : ""}{ch.name}</option>)}
                  </select>
                  {cid && (
                    <button data-testid={`sf-fav-${name}`} onClick={() => toggleFavorite(cid)}
                      className={`inline-flex items-center ${c?.favorite ? "text-[#FF9F2E]" : "text-[#64748B] hover:text-[#FF9F2E]"}`} title="Favorito">
                      <Star className="w-4 h-4" fill={c?.favorite ? "#FF9F2E" : "none"} />
                    </button>
                  )}
                  {c && (charReady(c)
                    ? <span className="inline-flex items-center gap-1 text-xs text-emerald-400" data-testid={`sf-speaker-ok-${name}`}><CheckCircle2 className="w-3.5 h-3.5" /> foto + voz listas</span>
                    : <span className="inline-flex items-center gap-1 text-xs text-[#FF9F2E]" data-testid={`sf-speaker-missing-${name}`}><AlertTriangle className="w-3.5 h-3.5" /> {c.voice_path ? "falta foto" : "falta voz"}</span>)}
                  {c && c.voice_path && (
                    <button data-testid={`sf-speaker-preview-${name}`} onClick={() => playVoicePreview(cid)} disabled={previewBusy === cid}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10 disabled:opacity-50">
                      {previewBusy === cid ? <Loader2 className="w-3.5 h-3.5 spin" /> : <Volume2 className="w-3.5 h-3.5" />} Escuchar voz
                    </button>
                  )}
                </div>
              );
            })}
            {characters.length === 0 && (
              <div className="rounded-xl border border-[#FF9F2E]/40 bg-[#FF9F2E]/10 px-4 py-3 text-xs text-[#FFCF8F]" data-testid="sf-no-characters">
                Todavía no tienes personajes. Ve a la pestaña <b>Personajes</b> para subir su foto y su voz.
              </div>
            )}
            {unreadySpeakers.length > 0 && (
              <div className="rounded-xl border border-[#FF9F2E]/40 bg-[#FF9F2E]/10 px-4 py-3 text-xs text-[#FFCF8F]" data-testid="sf-cast-warning">
                <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                {unreadySpeakers.join(", ")}: sin foto+voz esas líneas se generarán como ambiente (sin lip-sync). Prepáralos para el diálogo real en cámara.
              </div>
            )}
          </div>
        )}

        {step === 6 && (
          <div className="space-y-4 fade-up" data-testid="sf-step-cost">
            <div>
              <label className="mono-label">Costo exacto antes de generar</label>
              <p className="text-xs text-[#64748B] mt-1">Nada se genera hasta que confirmes. Solo pagas escenas entregadas; si una falla, se reembolsa.</p>
            </div>
            <div className="rounded-xl border border-white/8 bg-[#0B0C13] px-4 py-3 space-y-1.5">
              {costScenes.map((s, i) => (
                <div key={s.key} className="flex items-center justify-between text-xs text-[#94A3B8]" data-testid={`sf-cost-row-${i}`}>
                  <span className="inline-flex items-center gap-1.5">
                    {s.type === "dialogue" ? <MessageSquare className="w-3 h-3 text-[#00F0FF]" /> : <VideoIcon className="w-3 h-3 text-[#FF9F2E]" />}
                    Escena {i + 1} · {s.type === "dialogue" ? "diálogo (lip-sync nativo)" : "ambiente"} · {Number(s.duration || 6)}s
                  </span>
                  <span>{fmtCredits(filmSceneCredits(s))}</span>
                </div>
              ))}
              <div className="flex items-center justify-between border-t border-white/8 pt-2 mt-1">
                <span className="text-sm text-[#94A3B8]">Total ({dialogueCount} diálogo · {costScenes.length - dialogueCount} ambiente)</span>
                <strong className="text-white inline-flex items-center gap-1.5" data-testid="sf-cost-total">
                  <Coins className="w-4 h-4 text-[#FF9F2E]" /> {fmtCredits(totalCredits)} <span className="text-[#64748B] font-normal">({fmtCreditsUsd(totalCredits)})</span>
                </strong>
              </div>
            </div>
            {unreadySpeakers.length > 0 && (
              <div className="rounded-xl border border-[#FF9F2E]/40 bg-[#FF9F2E]/10 px-4 py-3 text-xs text-[#FFCF8F]" data-testid="sf-cost-warning">
                <AlertTriangle className="w-3.5 h-3.5 inline mr-1" /> Algunas líneas de diálogo se generarán como ambiente por falta de foto/voz del personaje.
              </div>
            )}
            <div className="rounded-xl border border-white/8 bg-[#0B0C13] p-4 space-y-2" data-testid="sf-bgm">
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF9F2E]"><Music className="w-4 h-4" /> Música de fondo (opcional)</span>
              <p className="text-xs text-[#64748B]">Suena por debajo de todo y baja el volumen automáticamente cuando alguien habla.</p>
              <div className="flex flex-wrap gap-2">
                {[["off", "Sin música"], ["mood", "Generar por mood"], ["upload", "Subir mi mp3"]].map(([v, l]) => (
                  <button key={v} data-testid={`sf-bgm-${v}`} onClick={() => setBgmMode(v)}
                    className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${bgmMode === v ? "bg-[#FF9F2E]/15 border-[#FF9F2E] text-[#FF9F2E]" : "border-white/12 text-[#64748B]"}`}>
                    {l}
                  </button>
                ))}
              </div>
              {bgmMode === "mood" && (
                <>
                  <input data-testid="sf-bgm-prompt" value={bgmPrompt} onChange={(e) => setBgmPrompt(e.target.value)}
                    placeholder="Ej. épica, tensa, neón, synthwave oscuro"
                    className="w-full bg-[#0B0C13] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#FF9F2E]" />
                  <div className="flex flex-wrap gap-1.5" data-testid="sf-bgm-genres">
                    {MUSIC_GENRES.map((g) => (
                      <button key={g.id} data-testid={`sf-bgm-genre-${g.id}`} onClick={() => setBgmPrompt(g.prompt)}
                        className="px-2.5 py-1 rounded-full border border-white/12 text-[11px] text-[#94A3B8] hover:border-[#FF9F2E] hover:text-[#FF9F2E] transition-colors">
                        {g.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
              {bgmMode === "upload" && (
                <div className="flex items-center gap-2">
                  <label className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-white/12 text-[#94A3B8] hover:border-[#FF9F2E] cursor-pointer">
                    {bgmUploading ? <Loader2 className="w-3.5 h-3.5 spin" /> : <Music className="w-3.5 h-3.5" />} {bgmPath ? "Cambiar mp3" : "Elegir mp3"}
                    <input type="file" accept="audio/*" className="hidden" data-testid="sf-bgm-upload" onChange={(e) => uploadBgm(e.target.files?.[0])} />
                  </label>
                  {bgmPath && <span className="text-xs text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> música lista</span>}
                </div>
              )}
              {bgmMode !== "off" && (
                <div className="flex items-center gap-3 pt-1" data-testid="sf-bgm-volume-row">
                  <span className="text-xs text-[#94A3B8] shrink-0 inline-flex items-center gap-1"><Volume2 className="w-3.5 h-3.5" /> Volumen</span>
                  <input type="range" min="0.05" max="0.9" step="0.05" value={bgmVolume} data-testid="sf-bgm-volume"
                    onChange={(e) => setBgmVolume(Number(e.target.value))} className="flex-1 accent-[#FF9F2E]" />
                  <span className="text-xs text-[#64748B] w-10 text-right">{Math.round(bgmVolume * 100)}%</span>
                </div>
              )}
            </div>
            <button type="button" data-testid="sf-auto-assemble" onClick={() => setAutoAssemble(!autoAssemble)}
              className={`w-full text-left p-4 rounded-xl border transition-colors ${autoAssemble ? "border-[#00F0FF] bg-[#00F0FF]/10" : "border-white/12 hover:border-white/25"}`}>
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 font-head font-semibold text-sm"><Film className="w-4 h-4 text-[#00F0FF]" /> Unir todo en un MP4 final</span>
                <span className={`w-9 h-5 rounded-full flex items-center px-0.5 transition-colors ${autoAssemble ? "bg-[#00F0FF] justify-end" : "bg-white/15"}`}>
                  <span className="w-4 h-4 rounded-full bg-white" />
                </span>
              </div>
              <p className="text-xs text-[#64748B] mt-1">Al terminar, une todas las escenas con cortes invisibles (fundido) en un solo video listo para guardar.</p>
            </button>
            <button type="button" data-testid="sf-burn-subs" onClick={() => setBurnSubs(!burnSubs)}
              className={`w-full text-left p-4 rounded-xl border transition-colors ${burnSubs ? "border-[#FF9F2E] bg-[#FF9F2E]/10" : "border-white/12 hover:border-white/25"}`}>
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 font-head font-semibold text-sm"><FileText className="w-4 h-4 text-[#FF9F2E]" /> Subtítulos incrustados</span>
                <span className={`w-9 h-5 rounded-full flex items-center px-0.5 transition-colors ${burnSubs ? "bg-[#FF9F2E] justify-end" : "bg-white/15"}`}>
                  <span className="w-4 h-4 rounded-full bg-white" />
                </span>
              </div>
              <p className="text-xs text-[#64748B] mt-1">Quema los diálogos/narración sobre el video final, ideal para redes sociales (se ven sin sonido).</p>
            </button>
            <button data-testid="sf-confirm-generate" onClick={submit} disabled={submitting || !costScenes.some((s) => s.description.trim())}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#FF334B] hover:bg-[#E0223A] text-white font-semibold py-3.5 rounded-full transition-colors disabled:opacity-60">
              {submitting ? <Loader2 className="w-5 h-5 spin" /> : <Sparkles className="w-5 h-5" />}
              Confirmar y generar · {fmtCredits(totalCredits)}
            </button>
          </div>
        )}

        {step === 7 && (
          <div className="space-y-4 fade-up" data-testid="sf-step-generate">
            {!current && <p className="text-sm text-[#94A3B8]">Preparando tu cortometraje…</p>}
            {current && (
              <>
                <div className="flex items-center justify-between">
                  <div className="font-head font-semibold text-lg">{current.title}</div>
                  <StatusBadge status={current.status} />
                </div>
                {current.status === "processing" && (
                  <div className="flex items-center gap-3 text-[#94A3B8]" data-testid="sf-progress">
                    <Loader2 className="w-5 h-5 spin text-[#FF9F2E]" />
                    {current.progress?.label || "En cola"}… ({current.progress?.current || 0}/{current.progress?.total || 0})
                  </div>
                )}
                {current.status === "failed" && <p className="text-red-400 text-sm" data-testid="sf-error">{current.error}</p>}
                {current.error && current.status === "ready" && <p className="text-[#FF9F2E] text-xs" data-testid="sf-partial-note">{current.error}</p>}
                {current.status === "ready" && (current.result?.export_path || current.result?.concat_path) && (
                  <div className="rounded-xl overflow-hidden border border-[#00F0FF]/40 bg-black" data-testid="sf-final-video">
                    <div className="px-3 py-2 flex items-center gap-2 bg-[#00F0FF]/10">
                      <Film className="w-4 h-4 text-[#00F0FF]" />
                      <span className="text-sm font-semibold text-[#00F0FF]">Película final (cortes invisibles)</span>
                    </div>
                    <video src={mediaUrl(current.result.export_path || current.result.concat_path)} controls className="w-full aspect-video object-contain bg-black" data-testid="sf-final-video-player" />
                    <div className="p-2">
                      <a data-testid="sf-final-download" href={mediaUrl(current.result.export_path || current.result.concat_path) + (mediaUrl(current.result.export_path || current.result.concat_path).includes("?") ? "&" : "?") + "dl=" + encodeURIComponent((current.title || "cortometraje") + ".mp4")}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full bg-[#00F0FF] text-black hover:opacity-90">
                        <Download className="w-4 h-4" /> Descargar MP4
                      </a>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(current.scenes || []).map((sc, i) => (
                    <div key={i} className="rounded-xl overflow-hidden border border-white/10 bg-black" data-testid={`sf-result-scene-${i}`}>
                      <div className="relative">
                        {sc.video_path
                          ? <video src={mediaUrl(sc.video_path)} controls className="w-full aspect-video object-cover" data-testid={`sf-result-video-${i}`} />
                          : <div className="w-full aspect-video flex items-center justify-center text-xs text-[#64748B]">
                              <Loader2 className="w-4 h-4 spin mr-1" /> escena {i + 1}
                            </div>}
                        {sc.type === "dialogue" && (
                          <span className="absolute top-1.5 right-1.5 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#00F0FF]/85 text-black">
                            <Mic className="w-2.5 h-2.5" /> diálogo
                          </span>
                        )}
                      </div>
                      {current.status === "ready" && (
                        <button data-testid={`sf-regen-scene-${i}`} onClick={() => regenerateScene(i)} disabled={regenBusy === i}
                          className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold py-2 bg-[#0B0C13] text-[#94A3B8] hover:text-[#FF334B] transition-colors disabled:opacity-50">
                          {regenBusy === i ? <Loader2 className="w-3.5 h-3.5 spin" /> : <Wand2 className="w-3.5 h-3.5" />} Regenerar escena {i + 1}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {current.status === "ready" && current.meta?.character_id && (
                  <div className="rounded-xl border border-white/8 bg-[#0B0C13] p-4 flex items-center justify-between gap-3" data-testid="sf-continuity">
                    <div className="flex items-center gap-2 min-w-0">
                      <ShieldCheck className="w-4 h-4 text-[#FF9F2E] shrink-0" />
                      <p className="text-[11px] text-[#64748B]">
                        {current.result?.continuity
                          ? `Continuidad ${current.result.continuity.overall}/100 · ${current.result.continuity.deviations || 0} desvío(s)`
                          : "Revisa que el personaje se mantenga consistente."}
                      </p>
                    </div>
                    <button data-testid="sf-continuity-check" onClick={runCheck} disabled={checking || !!current.busy}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold px-3.5 py-2 rounded-full border border-[#FF9F2E]/50 text-[#FF9F2E] hover:bg-[#FF9F2E]/10 transition-colors disabled:opacity-50 shrink-0">
                      {checking ? <Loader2 className="w-4 h-4 spin" /> : <ShieldCheck className="w-4 h-4" />} Analizar
                    </button>
                  </div>
                )}
                {current.status === "ready" && (
                  <div className="flex flex-wrap items-center gap-3">
                    <button data-testid="sf-download-srt" onClick={downloadSrt}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full border border-white/12 text-[#94A3B8] hover:border-[#FF334B] hover:text-[#FF334B] transition-colors">
                      <FileText className="w-4 h-4" /> Subtítulos (SRT)
                    </button>
                    <p className="text-xs text-[#64748B]">Ve a <span className="text-[#FF334B]">Exportar</span> para unir todo en un MP4 con cortes invisibles.</p>
                  </div>
                )}
                {current.status === "ready" && (
                  <div className="rounded-xl border border-white/8 bg-[#0B0C13] p-4 space-y-3" data-testid="sf-share-dub">
                    <div className="flex flex-wrap items-center gap-3">
                      <button data-testid="sf-publish" onClick={publishToCommunity} disabled={publishing}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full bg-[#00F0FF] text-black hover:opacity-90 transition-opacity disabled:opacity-50">
                        {publishing ? <Loader2 className="w-4 h-4 spin" /> : <Share2 className="w-4 h-4" />} Publicar en Comunidad
                      </button>
                      {publicLink && (
                        <a data-testid="sf-public-link" href={publicLink} target="_blank" rel="noreferrer"
                          className="text-xs text-[#00F0FF] underline truncate max-w-[220px]">{publicLink}</a>
                      )}
                    </div>
                    {publicLink && (
                      <div className="flex flex-wrap items-center gap-2" data-testid="sf-social">
                        <span className="text-xs text-[#64748B]">Compartir:</span>
                        <button data-testid="sf-share-whatsapp" onClick={() => shareTo("whatsapp")}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10">
                          <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                        </button>
                        <button data-testid="sf-share-instagram" onClick={() => shareTo("instagram")}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-pink-500/40 text-pink-400 hover:bg-pink-500/10">
                          <Instagram className="w-3.5 h-3.5" /> Instagram
                        </button>
                        <button data-testid="sf-share-tiktok" onClick={() => shareTo("tiktok")}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-white/20 text-white hover:bg-white/10">
                          <Music className="w-3.5 h-3.5" /> TikTok
                        </button>
                      </div>
                    )}
                    <div className="border-t border-white/8 pt-3">
                      <button data-testid="sf-cover-toggle" onClick={() => { setCoverOpen(!coverOpen); if (!coverPreview) setCoverPreview(current.result?.poster_path || ""); }}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF9F2E] hover:text-[#FFB25E]">
                        <Palette className="w-4 h-4" /> Editar portada
                      </button>
                      {coverOpen && (
                        <div className="mt-2 space-y-3" data-testid="sf-cover-editor">
                          <div>
                            <span className="text-xs text-[#64748B]">Fotograma (escena)</span>
                            <div className="flex gap-2 overflow-x-auto pb-1 mt-1">
                              {(current.scenes || []).filter((s) => s.video_path).map((s, i) => (
                                <button key={i} data-testid={`sf-cover-scene-${i}`} onClick={() => setCoverScene(i)}
                                  className={`shrink-0 w-24 rounded-lg overflow-hidden border-2 transition-colors ${coverScene === i ? "border-[#FF9F2E]" : "border-white/10"}`}>
                                  <video src={mediaUrl(s.video_path)} muted className="w-full aspect-video object-cover bg-black" />
                                </button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <span className="text-xs text-[#64748B]">Color del título</span>
                            <div className="flex gap-2 mt-1">
                              {[["blanco", "#F8FAFC"], ["rojo", "#FF334B"], ["cian", "#00F0FF"], ["ambar", "#FF9F2E"], ["morado", "#A03CFF"]].map(([v, hex]) => (
                                <button key={v} data-testid={`sf-cover-color-${v}`} onClick={() => setCoverColor(v)}
                                  className={`w-7 h-7 rounded-full border-2 ${coverColor === v ? "border-white" : "border-white/20"}`} style={{ backgroundColor: hex }} />
                              ))}
                            </div>
                          </div>
                          <button data-testid="sf-cover-generate" onClick={generateCover} disabled={coverBusy}
                            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full bg-[#FF9F2E] text-black hover:opacity-90 disabled:opacity-50">
                            {coverBusy ? <Loader2 className="w-4 h-4 spin" /> : <Palette className="w-4 h-4" />} Generar portada
                          </button>
                          {coverPreview && <img src={mediaUrl(coverPreview)} alt="portada" data-testid="sf-cover-preview" className="w-40 rounded-lg border border-white/10" />}
                        </div>
                      )}
                    </div>
                    <div className="border-t border-white/8 pt-3">
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF9F2E]"><Languages className="w-4 h-4" /> Doblar a otro idioma</span>
                      <p className="text-xs text-[#64748B] mt-1">Reutiliza el guion y regenera el corto con las mismas caras y voces. Gasta créditos como un corto nuevo.</p>
                      {!dubLang ? (
                        <div className="flex flex-wrap gap-2 mt-2">
                          <button data-testid="sf-dub-en" onClick={() => setDubLang("en")}
                            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full border border-[#FF9F2E]/50 text-[#FF9F2E] hover:bg-[#FF9F2E]/10 transition-colors">
                            <Languages className="w-4 h-4" /> Inglés
                          </button>
                          <button data-testid="sf-dub-spanglish" onClick={() => setDubLang("spanglish")}
                            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full border border-[#FF9F2E]/50 text-[#FF9F2E] hover:bg-[#FF9F2E]/10 transition-colors">
                            <Languages className="w-4 h-4" /> Spanglish
                          </button>
                          <button data-testid="sf-dub-both" onClick={() => setDubLang("both")}
                            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full bg-[#FF9F2E]/15 border border-[#FF9F2E] text-[#FF9F2E] hover:bg-[#FF9F2E]/25 transition-colors">
                            <Languages className="w-4 h-4" /> Inglés + Spanglish
                          </button>
                        </div>
                      ) : (
                        <div className="mt-2 rounded-lg border border-[#FF9F2E]/40 bg-[#FF9F2E]/10 p-3 space-y-2" data-testid="sf-dub-confirm">
                          <p className="text-xs text-[#FFCF8F]">
                            {dubLang === "both"
                              ? <>Doblar a <b>Inglés + Spanglish</b> (2 versiones) cuesta <b>{fmtCredits(filmTotalCredits(current.scenes || []) * 2)}</b> ({fmtCreditsUsd(filmTotalCredits(current.scenes || []) * 2)}). El Español ya es este corto.</>
                              : <>Doblar a <b>{dubLang === "en" ? "Inglés" : "Spanglish"}</b> cuesta <b>{fmtCredits(filmTotalCredits(current.scenes || []))}</b> ({fmtCreditsUsd(filmTotalCredits(current.scenes || []))}). Se regenera el video.</>}
                          </p>
                          <div className="flex gap-2">
                            <button data-testid="sf-dub-confirm-btn" onClick={() => dubFilm(dubLang)} disabled={dubbing}
                              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full bg-[#FF9F2E] text-black hover:opacity-90 disabled:opacity-50">
                              {dubbing ? <Loader2 className="w-4 h-4 spin" /> : <Languages className="w-4 h-4" />} Confirmar doblaje
                            </button>
                            <button data-testid="sf-dub-cancel" onClick={() => setDubLang("")}
                              className="text-sm font-semibold px-4 py-2 rounded-full border border-white/12 text-[#94A3B8] hover:text-white">Cancelar</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {step < 7 && (
          <div className="flex items-center justify-between pt-2 border-t border-white/8">
            <button data-testid="sf-nav-back" onClick={goBack} disabled={step === 1}
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full border border-white/12 text-[#94A3B8] hover:text-white disabled:opacity-30">
              <ArrowLeft className="w-4 h-4" /> Atrás
            </button>
            {step === 3 ? (
              <span className="text-xs text-[#64748B]">Genera las escenas para continuar →</span>
            ) : step === 6 ? (
              <span />
            ) : (
              <button data-testid="sf-nav-next" onClick={goNext} disabled={!canNext()}
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-5 py-2 rounded-full bg-[#FF334B] text-white hover:bg-[#E0223A] transition-colors disabled:opacity-40">
                Siguiente <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
